import { z } from 'zod';
import { AppLocale } from './locale.js';
import { Email } from './auth.js';
import { Password } from './onboarding.js';
/**
 * Login and password reset (PLAN.md §10).
 *
 * These are the two flows the funnel does not own: the account already exists
 * in the CRM, and everything here is a question we ask on the user's behalf and
 * a decision the CRM makes. What the contract adds is the shape of the answer —
 * uniform where the truth would leak whether an address holds an account
 * (finding #17), and explicit where the user has to be told what to do next.
 */
export const LoginRequest = z.object({
    email: Email,
    /**
     * Only length-bounded. Every other rule about what a password may be belongs
     * to the CRM, and a client-side policy on an EXISTING password rejects people
     * whose password predates the policy.
     */
    password: z.string().min(1, 'Enter your password.').max(256),
    turnstileToken: z.string().max(4096).optional(),
    locale: AppLocale.optional(),
});
/** The cabinet hand-off: a single-use, short-lived URL minted by the CRM. */
const LoginComplete = z.object({
    status: z.literal('ok'),
    url: z.string().url(),
});
/**
 * The second factor, when the account has one.
 *
 * The CRM's `twoFactorToken` never reaches the browser. It is held server-side
 * against this opaque `challengeId`, so a token that stands in for a
 * half-completed login cannot be replayed from anywhere else.
 */
const LoginTwoFactor = z.object({
    status: z.literal('two_factor'),
    challengeId: z.string(),
    expiresInSeconds: z.number().int().positive(),
    attemptsRemaining: z.number().int().nonnegative(),
    /** The CRM's own wording for where the code was sent, when it says. */
    message: z.string().optional(),
});
export const LoginResponse = z.discriminatedUnion('status', [LoginComplete, LoginTwoFactor]);
export const TwoFactorRequest = z.object({
    challengeId: z.string().min(1).max(64),
    /**
     * Permissive on shape: an authenticator app gives six digits, but recovery
     * codes are longer and not always numeric, and a code the CRM would have
     * accepted must not be refused by us before it gets there.
     */
    code: z
        .string()
        .trim()
        .min(4, 'Enter the code from your authenticator.')
        .max(32)
        .regex(/^[A-Za-z0-9-]+$/, 'Enter the code from your authenticator.'),
});
export const TwoFactorResponse = LoginComplete;
export const PasswordForgotRequest = z.object({
    email: Email,
    /** "I didn't receive it" — the next provider, not a repeat of the same one. */
    escalate: z.boolean().optional(),
    turnstileToken: z.string().max(4096).optional(),
    locale: AppLocale.optional(),
});
/**
 * Identical whether or not the address holds an account (finding #17).
 *
 * `forgot-password-pin.php:127` answered 404 `user_not_found` for an unknown
 * address, which made it a free membership oracle for the whole client base.
 * A challenge is issued either way; only the email is conditional.
 */
export const PasswordForgotResponse = z.object({
    challengeId: z.string(),
    expiresInSeconds: z.number().int().positive(),
    attemptsRemaining: z.number().int().nonnegative(),
    canEscalate: z.boolean(),
});
export const PasswordResetRequest = z.object({
    challengeId: z.string().min(1).max(64),
    code: z
        .string()
        .trim()
        .regex(/^\d{6}$/, 'Enter the 6-digit code.'),
    /** The new password, held to the same floor as the one step 1 sets. */
    password: Password,
});
export const PasswordResetResponse = z.object({
    ok: z.literal(true),
});
//# sourceMappingURL=login.js.map